/**
 * useWizardData Hook
 *
 * Fetches wizard dropdown options from Supabase:
 * - Trade types
 * - Job scopes (per trade)
 * - Permit types
 *
 * These are the database-driven options that populate the wizard steps.
 */

import { useQuery } from '@tanstack/react-query';
import {
  getTradeTypes,
  getJobScopes,
  getPermitTypes,
} from '@/lib/supabase';
import type {
  TradeType,
  JobScope,
  PermitType,
} from '@/integrations/supabase/types';

// Re-export types for convenience
export type { TradeType, JobScope, PermitType };

/**
 * Fetch all trade types
 * These are the main trade categories (Electrical, Plumbing, HVAC, etc.)
 */
export function useTradeTypes() {
  return useQuery({
    queryKey: ['wizard', 'tradeTypes'],
    queryFn: async (): Promise<TradeType[]> => {
      const { data, error } = await getTradeTypes();
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - trade types rarely change
  });
}

/**
 * Fetch job scopes for a specific trade
 * Job scopes define specific types of work within a trade
 * (e.g., "Panel Upgrade" within Electrical)
 */
export function useJobScopesForTrade(tradeTypeId: number | undefined) {
  return useQuery({
    queryKey: ['wizard', 'jobScopes', tradeTypeId],
    queryFn: async (): Promise<JobScope[]> => {
      if (!tradeTypeId) return [];
      const { data, error } = await getJobScopes(tradeTypeId);
      if (error) throw error;
      return data;
    },
    enabled: !!tradeTypeId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch job scopes for multiple trades at once
 * Useful for Step 3 when user has selected multiple trades
 */
export function useJobScopesForTrades(tradeTypeIds: number[]) {
  return useQuery({
    queryKey: ['wizard', 'jobScopes', 'batch', tradeTypeIds.sort().join(',')],
    queryFn: async (): Promise<Map<number, JobScope[]>> => {
      const scopesByTrade = new Map<number, JobScope[]>();

      // Fetch scopes for each trade in parallel
      const results = await Promise.all(
        tradeTypeIds.map(async (tradeId) => {
          const { data, error } = await getJobScopes(tradeId);
          if (error) {
            console.error(`Failed to fetch scopes for trade ${tradeId}:`, error);
            return { tradeId, scopes: [] };
          }
          return { tradeId, scopes: data };
        })
      );

      results.forEach(({ tradeId, scopes }) => {
        scopesByTrade.set(tradeId, scopes);
      });

      return scopesByTrade;
    },
    enabled: tradeTypeIds.length > 0,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch all permit types
 */
export function useAllPermitTypes() {
  return useQuery({
    queryKey: ['wizard', 'permitTypes'],
    queryFn: async (): Promise<PermitType[]> => {
      const { data, error } = await getPermitTypes();
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Get permit types for specific IDs
 * Useful for looking up permit types from job scope recommendations
 */
export function usePermitTypesByIds(permitTypeIds: number[]) {
  const { data: allPermitTypes, ...rest } = useAllPermitTypes();

  const filteredTypes = allPermitTypes?.filter(
    (pt) => permitTypeIds.includes(pt.id)
  ) || [];

  return {
    data: filteredTypes,
    allPermitTypes,
    ...rest,
  };
}

/**
 * Combined hook for all wizard data needs
 * Provides a single interface for all wizard dropdown options
 */
export function useWizardData(options: {
  selectedTradeIds?: number[];
} = {}) {
  const { selectedTradeIds = [] } = options;

  const tradeTypes = useTradeTypes();
  const jobScopes = useJobScopesForTrades(selectedTradeIds);
  const permitTypes = useAllPermitTypes();

  const isLoading = tradeTypes.isLoading || jobScopes.isLoading || permitTypes.isLoading;
  const isError = tradeTypes.isError || jobScopes.isError || permitTypes.isError;

  return {
    // Data
    tradeTypes: tradeTypes.data || [],
    jobScopes: jobScopes.data || new Map(),
    permitTypes: permitTypes.data || [],

    // Loading states
    isLoading,
    isError,
    tradeTypesLoading: tradeTypes.isLoading,
    jobScopesLoading: jobScopes.isLoading,
    permitTypesLoading: permitTypes.isLoading,

    // Error states
    tradeTypesError: tradeTypes.error,
    jobScopesError: jobScopes.error,
    permitTypesError: permitTypes.error,

    // Refetch functions
    refetchTradeTypes: tradeTypes.refetch,
    refetchJobScopes: jobScopes.refetch,
    refetchPermitTypes: permitTypes.refetch,
  };
}

/**
 * Helper to get trade by ID
 */
export function getTradeById(trades: TradeType[], id: number): TradeType | undefined {
  return trades.find((t) => t.id === id);
}

/**
 * Helper to get trade by code
 */
export function getTradeByCode(trades: TradeType[], code: string): TradeType | undefined {
  return trades.find((t) => t.code === code);
}

/**
 * Helper to get job scope by ID
 */
export function getJobScopeById(scopes: JobScope[], id: number): JobScope | undefined {
  return scopes.find((s) => s.id === id);
}

/**
 * Helper to get permit type by ID
 */
export function getPermitTypeById(permitTypes: PermitType[], id: number): PermitType | undefined {
  return permitTypes.find((pt) => pt.id === id);
}
